import { DateTime } from "luxon";
import React from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import "./article-page.scss";
import { Col, Row } from "../components/grid";
import { Link, useLocation, useNavigate } from "react-router";
import Container from "../components/Container";
import Tag from "../components/Tag";
import { getFullPostImage } from "../utils/getPostImage";
import { useQuery } from "@tanstack/react-query";
import {
  fetchApi,
  createQueryKey,
  longCacheOptions,
  shortCacheOptions,
} from "../utils/apiUtils";
import { ArticlesSidebar } from "./blocks/ArticlesSidebar";
import ReadMore from "../components/ReadMore";
import { Gallery } from "../components/Gallery";
import LoadingOverlay from "../components/LoadingOverlay";

function Default(props) {
  const navigate = useNavigate();
  const slug = useLocation().pathname.split("/").at(-1);

  const {
    data: article,
    isLoading: articleLoading,
    error: articleError,
  } = useQuery({
    queryKey: createQueryKey("/wp-json/wp/v2/posts", { slug }),
    queryFn: () => fetchApi("/wp-json/wp/v2/posts?slug=" + slug),
    ...longCacheOptions,
  });

  const {
    data: news,
    isLoading: newsLoading,
    error: newsError,
  } = useQuery({
    queryKey: createQueryKey("/wp-json/wp/v2/posts", { per_page: 3 }),
    queryFn: () => fetchApi("/wp-json/wp/v2/posts?per_page=3"),
    ...shortCacheOptions,
  });

  return (
    <>
      <LoadingOverlay isLoaded={!articleLoading && article} />
      <div className="article-page">
        {!articleLoading && article && props.categoriesFetched && (
          <Container>
            <Row>
              <Col xl={8}>
                <Row>
                  <div className="article-header">
                    {DateTime.fromISO(article[0].date).toFormat("dd/MM/yyyy")}
                  </div>
                </Row>
                <Row>
                  <h1>{article[0].title.rendered}</h1>
                </Row>
                <Row>
                  <div className="post-tags">
                    {article[0].categories.map((catId, key) => {
                      const categoryName = props.categories.find(
                        (category) => category.id === catId,
                      )?.name;
                      return (
                        <Tag
                          key={key}
                          onClick={() => {
                            props.updateSelectedCategory(catId);
                            navigate("/actualites");
                          }}
                        >
                          {categoryName}
                        </Tag>
                      );
                    })}
                  </div>
                </Row>
                <Row>
                  <img
                    src={getFullPostImage(article[0])}
                    className="post-img"
                    alt={article[0].slug}
                  />
                </Row>
                <Row>
                  <div
                    className="article-content"
                    dangerouslySetInnerHTML={{
                      __html: article[0].content.rendered,
                    }}
                  />
                </Row>
                {article[0].acf.image_gallery.length !== 0 && (
                  <Row>
                    <Gallery pictures={article[0].acf.image_gallery} />
                  </Row>
                )}
                <Row className="posts-navigation">
                  <div className="previous">
                    {article[0].previous && (
                      <Link to={"/actualites/" + article[0].previous.slug}>
                        <FaChevronLeft />
                        Article précédent
                      </Link>
                    )}
                  </div>
                  <div className="next">
                    {article[0].next && (
                      <Link to={"/actualites/" + article[0].next.slug}>
                        Article suivant
                        <FaChevronRight />
                      </Link>
                    )}
                  </div>
                </Row>
              </Col>
              <Col xl={4}>
                <ArticlesSidebar
                  categories={props.categories}
                  categoriesFetched={props.categoriesFetched}
                  onTagSelection={(tagId) => {
                    props.updateSelectedCategory(tagId);
                    props.updateSearch("");
                    navigate("/actualites");
                  }}
                  onSearch={(searchText) => {
                    props.updateSelectedCategory(0);
                    props.updateSearch(searchText);
                    navigate("/actualites");
                  }}
                >
                  <h2>Dernières actualités</h2>
                  <div className="news-list">
                    {!newsLoading &&
                      news &&
                      news.map((post, key) => (
                        <article key={key}>
                          <Link to={"/actualites/" + post.slug}>
                            <h3>{post.title.rendered}</h3>
                          </Link>
                          <div
                            className="excerpt"
                            dangerouslySetInnerHTML={{
                              __html: post.excerpt.rendered,
                            }}
                          />
                          <ReadMore to={"/actualites/" + post.slug} />
                        </article>
                      ))}
                  </div>
                  <div className="all-news">
                    <Link to="/actualites">
                      <button>Voir toutes les actualités</button>
                    </Link>
                  </div>
                </ArticlesSidebar>
              </Col>
            </Row>
          </Container>
        )}
      </div>
    </>
  );
}

export const ArticlePage = React.memo(Default);
